import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <section 
      id="how-it-works" 
      ref={ref}
      className="section-spacing relative bg-white"
      data-testid="how-it-works-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.howItWorks.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
            {t.howItWorks.title}
          </h2>
        </motion.div>
        
        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#2563EB]/30 via-[#2563EB]/20 to-transparent hidden md:block" />
          
          <div className="space-y-12 lg:space-y-24">
            {t.howItWorks.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-12 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`step-${index}`}
              >
                {/* Number Badge */}
                <div className="absolute left-0 lg:left-1/2 lg:-translate-x-1/2 w-16 h-16 rounded-full bg-white border-2 border-[#2563EB] flex items-center justify-center z-10 shadow-lg">
                  <span className="text-[#2563EB] font-bold text-lg">{step.number}</span>
                </div>
                
                {/* Content */}
                <div className={`flex-1 pl-24 lg:pl-0 ${index % 2 === 1 ? 'lg:text-right lg:pr-20' : 'lg:pl-20'}`}>
                  <div className={`card-light rounded-2xl p-6 lg:p-8 ${index % 2 === 1 ? 'lg:ml-auto' : ''} max-w-lg`}>
                    <h3 className="text-xl lg:text-2xl font-bold text-[#0F172A] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-[#475569] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
                
                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden lg:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
